import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { DataGrid, gridClasses } from "@mui/x-data-grid";
import dayjs from "dayjs";
import { HttpService } from "../../../service/HttpService";
import FullScreenLoader from "../../../components/Loader/FullScreenLoader";
import { useAuth } from "../../../context/AuthProvider";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";

const RockwoolReport = () => {
  const { setShowLoginPopup, showLoginPopup } = useAuth();
  const httpService = useMemo(
    () => new HttpService(setShowLoginPopup),
    [setShowLoginPopup]
  );
  const dateFilterData = JSON.parse(
    localStorage.getItem("rockwoolReportDateFilter") || "null"
  );

  const hasValidStoredStart =
    dateFilterData?.startDate && dayjs(dateFilterData.startDate).isValid();
  const hasValidStoredEnd =
    dateFilterData?.endDate && dayjs(dateFilterData.endDate).isValid();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageSize, setPageSize] = useState(15);
  const [page, setPage] = useState(0);
  const [startDate, setStartDate] = useState(
    hasValidStoredStart && hasValidStoredEnd ? dateFilterData.startDate : ""
  );
  const [endDate, setEndDate] = useState(
    hasValidStoredStart && hasValidStoredEnd ? dateFilterData.endDate : ""
  );
  const [rowCount, setRowCount] = useState(0);

  // History table state
  const [historyRows, setHistoryRows] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPage, setHistoryPage] = useState(0);
  const [historyPageSize, setHistoryPageSize] = useState(15);
  const [historyRowCount, setHistoryRowCount] = useState(0);
  const [search, setSearch] = useState("");
  const [selectedPlantId, setSelectedPlantId] = useState(null);

  const isMountedRef = useRef(false);
  const historySectionRef = useRef(null);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const scrollToHistorySection = useCallback(() => {
    if (historySectionRef.current) {
      historySectionRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, []);

  // Ensure end date is never before start date
  useEffect(() => {
    if (
      startDate &&
      endDate &&
      dayjs(endDate).isValid() &&
      dayjs(startDate).isValid() &&
      dayjs(endDate).isBefore(dayjs(startDate), "day")
    ) {
      const clearedEnd = "";
      setEndDate(clearedEnd);
      const rockwoolReportDateFilter = {
        startDate,
        endDate: clearedEnd,
      };
      localStorage.setItem(
        "rockwoolReportDateFilter",
        JSON.stringify(rockwoolReportDateFilter)
      );
    }
  }, [startDate]);

  const fetchRockwoolReport = async () => {
    setLoading(true);
    try {
      let url =
        `rockwool/rockwool-rooting-report` +
        `?page=${page + 1}&pageSize=${pageSize}`;

      // Append date filters only when both dates are valid
      const hasValidStart = !!startDate && dayjs(startDate).isValid();
      const hasValidEnd = !!endDate && dayjs(endDate).isValid();
      if (hasValidStart && hasValidEnd) {
        url += `&start_date=${startDate}&end_date=${endDate}`;
      }

      const res = await httpService.get(url);
      const list = Array.isArray(res?.data?.data)
        ? res.data.data
        : Array.isArray(res?.data)
        ? res.data
        : [];
      const total = res?.data?.pagination?.total || list.length || 0;

      if (!isMountedRef.current) return;

      setRows(list);
      setRowCount(total);
    } catch (_) {
      if (!isMountedRef.current) return;
      setRows([]);
      setRowCount(0);
    } finally {
      if (!isMountedRef.current) return;
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRockwoolReport();
  }, [page, pageSize, startDate, endDate, showLoginPopup]);

  const fetchRockwoolHistory = async () => {
    setHistoryLoading(true);
    try {
      let url =
        `rockwool/rooting-info` +
        `?page=${historyPage + 1}&pageSize=${historyPageSize}` +
        `&search=${search}`;

      // Attach plant_id filter when a cultivar is selected
      if (selectedPlantId && !search) {
        url += `&plant_id=${selectedPlantId}`;
      }

      // Append date filters only when both dates are valid and no search term is used
      const hasValidStart =
        !search && !!startDate && dayjs(startDate).isValid();
      const hasValidEnd = !search && !!endDate && dayjs(endDate).isValid();
      if (hasValidStart && hasValidEnd) {
        url += `&start_date=${startDate}&end_date=${endDate}`;
      }
      const res = await httpService.get(url);
      const list = Array.isArray(res?.data?.data)
        ? res.data.data
        : Array.isArray(res?.data)
        ? res.data
        : [];
      const total = res?.data?.pagination?.total || list.length || 0;

      if (!isMountedRef.current) return;

      setHistoryRows(list);
      setHistoryRowCount(total);
    } catch (_) {
      if (!isMountedRef.current) return;
      setHistoryRows([]);
      setHistoryRowCount(0);
    } finally {
      if (!isMountedRef.current) return;
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    fetchRockwoolHistory();
  }, [
    historyPage,
    historyPageSize,
    showLoginPopup,
    startDate,
    endDate,
    search,
    selectedPlantId,
  ]);

  // Prepare chart data from rows
  const chartData = useMemo(() => {
    return rows.map((row) => ({
      cultivar: row.cultivar || "-",
      plantId: row.plant_id,
      partiallyRooted:
        row.partially_rooted != null ? Number(row.partially_rooted) : 0,
      fullyRooted: row.fully_rooted != null ? Number(row.fully_rooted) : 0,
    }));
  }, [rows]);

  const columns = [
    {
      field: "cultivar",
      headerName: "Cultivar Name",
      flex: 1,
      minWidth: 190,
    },
    {
      field: "partially_rooted",
      headerName: "Days of Initial Rooting",
      flex: 1,
      minWidth: 190,
      renderCell: (params) => (
        <div style={{ padding: "10px" }}>
          {params.row?.partially_rooted != null &&
          params.row.partially_rooted !== "-"
            ? Number(params.row.partially_rooted)
            : "-"}
        </div>
      ),
    },
    {
      field: "fully_rooted",
      headerName: "Days of Fully Rooted",
      flex: 1,
      minWidth: 180,
      renderCell: (params) => (
        <div style={{ padding: "10px" }}>
          {params.row?.fully_rooted != null && params.row.fully_rooted !== "-"
            ? Number(params.row.fully_rooted)
            : "-"}
        </div>
      ),
    },
    // {
    //   field: "average_days",
    //   headerName: "Average Days",
    //   flex: 1,
    //   minWidth: 160,
    //   renderCell: (params) => (
    //     <div style={{ padding: "10px" }}>
    //       {params.row?.average_days != null
    //         ? Number(params.row.average_days).toFixed(2)
    //         : "-"}
    //     </div>
    //   ),
    //     },
  ];

  // History table columns
  const historyColumns = [
    {
      field: "cultivar",
      headerName: "Cultivar Name",
      flex: 1,
      minWidth: 190,
      renderCell: (params) => (
        <>
          <div style={{ padding: "10px" }}>{params?.row?.cultivar}</div>
        </>
      ),
    },
    // { field: "tray_id", headerName: "Tray ID", flex: 1, minWidth: 140 },
    {
      field: "clone_id",
      headerName: "Clone ID",
      flex: 1,
      minWidth: 140,
    },
    {
      field: "first_partially_rooted",
      headerName: "Date Of Initial Rooting",
      flex: 1,
      minWidth: 160,
      renderCell: (params) => (
        <>
          <div style={{ padding: "10px" }}>
            {params.row?.first_partially_rooted
              ? dayjs(params.row.first_partially_rooted).format("MM-DD-YYYY")
              : "-"}
          </div>
        </>
      ),
    },
    {
      field: "first_fully_rooted",
      headerName: "Date Of Fully Rooted",
      flex: 1,
      minWidth: 160,
      renderCell: (params) => (
        <>
          <div style={{ padding: "10px" }}>
            {params.row?.first_fully_rooted
              ? dayjs(params.row.first_fully_rooted).format("MM-DD-YYYY")
              : "-"}
          </div>
        </>
      ),
    },
    // {
    //   field: "before_weight",
    //   headerName: "Before Weight",
    //   flex: 1,
    //   minWidth: 190,
    //   renderHeader: () => (
    //     <>
    //       <div className="MuiDataGrid-columnHeaderTitle">Before Weight</div>
    //       <div className="ps-1" style={{ fontSize: "12px" }}>
    //         Grams
    //       </div>
    //     </>
    //   ),
    // },
    // {
    //   field: "after_weight",
    //   headerName: "After Weight",
    //   flex: 1,
    //   minWidth: 190,
    //   renderHeader: () => (
    //     <>
    //       <div className="MuiDataGrid-columnHeaderTitle">After Weight</div>
    //       <div className="ps-1" style={{ fontSize: "12px" }}>
    //         Grams
    //       </div>
    //     </>
    //   ),
    // },
    // {
    //   field: "rooting_status",
    //   headerName: "Rooting Status",
    //   flex: 1,
    //   minWidth: 160,
    // },
    // {
    //   field: "employee_name",
    //   headerName: "Employee Name",
    //   flex: 1,
    //   minWidth: 190,
    // },
  ];

  return (
    <>
      {loading && <FullScreenLoader />}
      <div className="maintenance-view">
        <div className="page-title">
          <div className="title-wrap">
            <h1>Average Rooting Days Report</h1>
          </div>
        </div>

        <div className="table-filter-wrap d-flex justify-content-between pb-0 pt-4">
          <div className="header_search-wrap">
            <div className="header_search_bar common_content">
              <div
                className="input-group input_group"
                style={{ display: "flex", gap: "12px", alignItems: "center" }}
              >
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Start Date"
                    value={startDate ? dayjs(startDate) : null}
                    disableFuture
                    onChange={(newStartDate) => {
                      if (!newStartDate) {
                        setStartDate("");
                        const rockwoolReportDateFilter = {
                          startDate: "",
                          endDate,
                        };
                        localStorage.setItem(
                          "rockwoolReportDateFilter",
                          JSON.stringify(rockwoolReportDateFilter)
                        );
                        setPage(0);
                        return;
                      }

                      const formattedDate =
                        dayjs(newStartDate).format("YYYY-MM-DD");
                      setStartDate(formattedDate);
                      const rockwoolReportDateFilter = {
                        startDate: formattedDate,
                        endDate,
                      };
                      localStorage.setItem(
                        "rockwoolReportDateFilter",
                        JSON.stringify(rockwoolReportDateFilter)
                      );
                      setPage(0);
                    }}
                    format="YYYY-MM-DD"
                    sx={{ minWidth: "200px" }}
                  />
                  <DatePicker
                    label="End Date"
                    value={endDate ? dayjs(endDate) : null}
                    disableFuture
                    minDate={
                      startDate && dayjs(startDate).isValid()
                        ? dayjs(startDate)
                        : undefined
                    }
                    onChange={(newEndDate) => {
                      if (!newEndDate) {
                        setEndDate("");
                        const rockwoolReportDateFilter = {
                          startDate,
                          endDate: "",
                        };
                        localStorage.setItem(
                          "rockwoolReportDateFilter",
                          JSON.stringify(rockwoolReportDateFilter)
                        );
                        setPage(0);
                        return;
                      }

                      const formattedDate =
                        dayjs(newEndDate).format("YYYY-MM-DD");
                      setEndDate(formattedDate);
                      const rockwoolReportDateFilter = {
                        startDate,
                        endDate: formattedDate,
                      };
                      localStorage.setItem(
                        "rockwoolReportDateFilter",
                        JSON.stringify(rockwoolReportDateFilter)
                      );
                      setPage(0);
                    }}
                    format="YYYY-MM-DD"
                    sx={{ minWidth: "200px" }}
                  />
                </LocalizationProvider>
              </div>
            </div>
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "24px",
            marginTop: "12px",
          }}
        >
          {/* Table Section - 50% */}
          <div>
            <div
              style={{
                height: 480,
                minHeight: "480px",
                width: "100%",
              }}
            >
              <DataGrid
                rows={rows}
                getRowId={(r) =>
                  r.id ||
                  r._id ||
                  r.clone_id ||
                  `${r.cultivar}-${Math.random()}`
                }
                columns={columns}
                loading={loading}
                rowCount={rowCount}
                pageSizeOptions={[15, 50, 100]}
                paginationModel={{ pageSize, page }}
                paginationMode="server"
                onPaginationModelChange={({ page, pageSize }) => {
                  setPage(page);
                  setPageSize(pageSize);
                }}
                getRowHeight={() => "auto"}
                sx={{
                  border: "1px solid #E0E0E0",
                  [`& .${gridClasses.cell}`]: { py: 0.5 },
                  "& .MuiDataGrid-row": {
                    cursor: "pointer",
                  },
                }}
                autoHeight
                showCellVerticalBorder
                showColumnVerticalBorder
                onRowClick={(params) => {
                  if (params?.row?.plant_id) {
                    setSelectedPlantId(params.row.plant_id);
                    setHistoryPage(0);
                  }
                  scrollToHistorySection();
                }}
              />
            </div>
          </div>

          {/* Chart Section - 50% */}
          <div>
            <div
              style={{
                backgroundColor: "#fff",
                border: "1px solid #E0E0E0",
                borderRadius: "4px",
                padding: "20px",
                height: 480,
                minHeight: "480px",
              }}
            >
              <h3
                style={{
                  marginBottom: "20px",
                  fontSize: "18px",
                  fontWeight: 600,
                  color: "#333",
                }}
              >
                Rooting Days
              </h3>
              {chartData.length > 0 ? (
                <LineChart
                  width={600}
                  height={400}
                  data={chartData}
                  margin={{
                    top: 5,
                    right: 30,
                    left: 20,
                    bottom: 60,
                  }}
                  style={{ cursor: "pointer" }}
                  onClick={(chartEvent) => {
                    const payload =
                      chartEvent?.activePayload && chartEvent.activePayload[0];
                    const plantIdFromPoint = payload?.payload?.plantId;
                    if (plantIdFromPoint) {
                      setSelectedPlantId(plantIdFromPoint);
                      setHistoryPage(0);
                    }
                    scrollToHistorySection();
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="cultivar"
                    angle={-45}
                    textAnchor="end"
                    height={80}
                    interval={0}
                    style={{ fontSize: "12px" }}
                  />
                  <YAxis
                    label={{
                      value: "Days",
                      angle: -90,
                      position: "insideLeft",
                    }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#fff",
                      border: "1px solid #ccc",
                      borderRadius: "4px",
                    }}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="partiallyRooted"
                    name="Days of Initial Rooting"
                    stroke="#8884d8"
                    strokeWidth={2}
                    dot={{ r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="fullyRooted"
                    name="Days of Fully Rooted"
                    stroke="#00A272"
                    strokeWidth={2}
                    dot={{ r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                  {/* When clicking on a point in the chart, filter history by that cultivar's plant_id */}
                  <Tooltip />
                </LineChart>
              ) : (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "600px",
                    height: "400px",
                    color: "#999",
                  }}
                >
                  No data available for chart
                </div>
              )}
            </div>
          </div>
        </div>

        {/* History Table Section */}
        <div style={{ marginTop: "32px" }} ref={historySectionRef}>
          <div className="page-title">
            <div className="title-wrap">
              <h1>Rooting Information</h1>
            </div>
          </div>
          <div className="table-filter-wrap d-flex justify-content-between pb-0 pt-4">
            <div className="header_search-wrap">
              <div className="header_search_bar common_content">
                <div className="input-group input_group">
                  <button className="btn search-btn search_btn">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="15"
                      height="15"
                      viewBox="0 0 21 21"
                      fill="none"
                    >
                      <circle
                        cx="9.98856"
                        cy="9.98856"
                        r="8.98856"
                        stroke="#495057"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M16.2402 16.7071L19.7643 20.222"
                        stroke="#495057"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                  <input
                    type="search"
                    className="form-control form_control"
                    placeholder="Search..."
                    aria-label="Search"
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setHistoryPage(0);
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
          <div>
            <div
              style={{
                height: `${historyRows?.length > 0 ? "" : "200px"}`,
                width: "100%",
                marginTop: 12,
              }}
            >
              <DataGrid
                rows={historyRows}
                columns={historyColumns}
                loading={historyLoading}
                rowCount={historyRowCount}
                pageSizeOptions={[15, 50, 100]}
                paginationModel={{
                  pageSize: historyPageSize,
                  page: historyPage,
                }}
                paginationMode="server"
                onPaginationModelChange={({ page, pageSize }) => {
                  setHistoryPage(page);
                  setHistoryPageSize(pageSize);
                }}
                getRowHeight={() => "auto"}
                sx={{
                  border: "1px solid #E0E0E0",
                  [`& .${gridClasses.cell}`]: { py: 0.5 },
                }}
                autoHeight
                showCellVerticalBorder
                showColumnVerticalBorder
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default RockwoolReport;
